import {
  Box,
  ButtonBase,
  CircularProgress,
  Drawer,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { getUserInfo, updateAvatar, regenerateAvatar } from "../../../../services/me";
import { useTelegram } from "../../../../hooks";
import { IS_WEB } from "../../../../utils/runtime";

function formatPhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length !== 11) return raw;
  const p = digits;
  return `+${p[0]} ${p.slice(1, 4)} ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9, 11)}`;
}

export function ProfileHeader() {
  const navigate = useNavigate();
  const { user: tgUser } = useTelegram();
  const { t } = useTranslation(["profile", "home"]);
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data } = useQuery({
    queryFn: getUserInfo,
    queryKey: ["me"],
    refetchOnWindowFocus: false,
  });

  const username =
    tgUser?.username
      ? `@${tgUser.username}`
      : data?.username
        ? `@${data.username}`
        : [data?.first_name, data?.last_name].filter(Boolean).join(" ") ||
          [tgUser?.first_name, tgUser?.last_name].filter(Boolean).join(" ") ||
          t("home:topbar.profile");

  const phone = formatPhone(data?.phone_number);
  const avatarUrl = data?.avatar_url || tgUser?.photo_url;
  const tgPhotoUrl = tgUser?.photo_url;
  const initial =
    data?.first_name?.[0]?.toUpperCase() ||
    tgUser?.first_name?.[0]?.toUpperCase() ||
    tgUser?.username?.[0]?.toUpperCase() ||
    "?";

  const onSuccess = () => {
    qc.invalidateQueries(["me"]);
    enqueueSnackbar(t("profile:avatar.updated"), { variant: "success" });
    setDrawerOpen(false);
  };
  const onError = () => {
    enqueueSnackbar(t("profile:avatar.failed"), { variant: "error" });
  };

  const regenerateMut = useMutation(regenerateAvatar, { onSuccess, onError });
  const setTgMut = useMutation(
    () => updateAvatar(tgPhotoUrl ?? null),
    { onSuccess, onError },
  );

  const isMutating = regenerateMut.isLoading || setTgMut.isLoading;

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: "8px",
        paddingBottom: "18px",
      }}
    >
      {IS_WEB && (
        <ButtonBase
          onClick={() => navigate(-1 as any)}
          aria-label="back"
          sx={(theme) => ({
            position: "absolute",
            top: 0,
            left: 0,
            width: 32,
            height: 32,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(0, 0, 0, 0.05)",
            color: theme.palette.text.primary,
            transition: "background 0.15s, transform 0.1s",
            "&:hover": {
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.10)"
                  : "rgba(0, 0, 0, 0.08)",
            },
            "&:active": { transform: "scale(0.94)" },
          })}
        >
          <Icon icon="solar:arrow-left-linear" width={16} height={16} />
        </ButtonBase>
      )}

      <ButtonBase
        onClick={() => navigate("/personal-data")}
        aria-label={t("profile:settings_aria")}
        sx={(theme) => ({
          position: "absolute",
          top: 0,
          right: 0,
          width: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: theme.palette.text.primary,
          transition: "opacity 0.2s ease-in-out",
          "&:active": { transform: "scale(0.9)", opacity: 0.7 },
        })}
      >
        <Icon
          icon="solar:settings-outline"
          width={24}
          height={24}
          color="currentColor"
        />
      </ButtonBase>

      <ButtonBase
        onClick={() => setDrawerOpen(true)}
        sx={{ borderRadius: "999px", position: "relative" }}
      >
        <Box
          sx={(theme) => ({
            width: 96,
            height: 96,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "38px",
            fontWeight: 700,
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
            backgroundImage: avatarUrl ? `url(${avatarUrl})` : "none",
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 8px 24px rgba(0, 0, 0, 0.35)"
                : "0 8px 24px rgba(51, 119, 255, 0.18)",
            border:
              theme.palette.mode === "dark"
                ? "3px solid rgba(255, 255, 255, 0.06)"
                : "3px solid #FFFFFF",
          })}
        >
          {avatarUrl ? "" : initial}
        </Box>
        <Box
          sx={(theme) => ({
            position: "absolute",
            bottom: 0,
            right: 0,
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: theme.palette.primary.main,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `2px solid ${theme.palette.background.default}`,
          })}
        >
          <Icon icon="solar:camera-bold" width={14} height={14} color="#fff" />
        </Box>
      </ButtonBase>

      <Typography
        sx={{
          marginTop: "14px",
          fontSize: "22px",
          fontWeight: 700,
          textAlign: "center",
          maxWidth: "80%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {username}
      </Typography>

      {phone && (
        <Typography
          sx={{
            marginTop: "4px",
            fontSize: "14px",
            fontWeight: 500,
            color: "text.secondary",
          }}
        >
          {phone}
        </Typography>
      )}

      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => !isMutating && setDrawerOpen(false)}
        sx={{ zIndex: 100010 }}
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.55)" } },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "secondary.main",
            borderTopLeftRadius: "24px",
            borderTopRightRadius: "24px",
            padding: "10px 16px 28px",
            backgroundImage: "none",
          },
        }}
      >
        {/* Handle bar */}
        <Box
          sx={(theme) => ({
            width: "44px",
            height: "4px",
            borderRadius: "4px",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(0, 0, 0, 0.2)",
            margin: "0 auto 14px",
          })}
        />

        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            textAlign: "center",
            marginBottom: "18px",
            color: "text.primary",
          }}
        >
          {t("profile:avatar.title")}
        </Typography>

        <Box sx={{ display: "grid", gap: "10px" }}>
          <AvatarOption
            icon="solar:refresh-bold"
            label={t("profile:avatar.regenerate")}
            loading={regenerateMut.isLoading}
            disabled={isMutating}
            onClick={() => regenerateMut.mutate()}
          />
          {tgPhotoUrl && (
            <AvatarOption
              icon="solar:plain-2-bold"
              label={t("profile:avatar.use_telegram")}
              loading={setTgMut.isLoading}
              disabled={isMutating}
              onClick={() => setTgMut.mutate()}
              previewUrl={tgPhotoUrl}
            />
          )}
        </Box>
      </Drawer>
    </Box>
  );
}

function AvatarOption({
  icon,
  label,
  loading,
  disabled,
  onClick,
  previewUrl,
}: {
  icon: string;
  label: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
  previewUrl?: string;
}) {
  return (
    <ButtonBase
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: "14px",
        width: "100%",
        padding: "14px",
        borderRadius: "18px",
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.04)"
            : "rgba(0, 0, 0, 0.04)",
        opacity: disabled && !loading ? 0.5 : 1,
        transition: "background 0.15s",
        "&:active": {
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.08)"
              : "rgba(0, 0, 0, 0.08)",
        },
      })}
    >
      {previewUrl ? (
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            backgroundImage: `url(${previewUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          sx={(theme) => ({
            width: 42,
            height: 42,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(0, 0, 0, 0.06)",
            color: theme.palette.primary.main,
            flexShrink: 0,
          })}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "inherit" }} />
          ) : (
            <Icon icon={icon} width={22} height={22} color="currentColor" />
          )}
        </Box>
      )}
      <Typography
        sx={{
          flex: 1,
          fontSize: "15px",
          fontWeight: 600,
          color: "text.primary",
          textAlign: "left",
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
}
